'use client';

// The queue status the page, the bar and the sidebar share (BL-2685), derived once. Kept apart from the
// provider so it stays small, and so an unmapped native failure code is reported from an effect, once
// per code, never from inside a memo.

import { useEffect, useMemo, useRef } from 'react';
import { deriveQueueStatus, type NativeQueueInput, type QueueStatus } from '@auctogon/admin-isomorphic/isomorphic/offline/queue-status';

/** A native failure code with no wording yet reaches the logs, so the reason map gets an entry. */
function reportUnknownFailureCode(code: string | null): void {
  // eslint-disable-next-line no-console -- client module; createLogger is server-only (ALS).
  console.error('change queue: a failure code has no wording', new Error(`unmapped native failure code ${code ?? 'none'}`));
}

/**
 * @param input.groups - the JS queue's grouped entries.
 * @param input.native - the native queue's summary and failed rows, or null.
 * @param input.ready - false until both queues have been read (or while an error shows instead).
 * @returns the derived status, or null while not ready.
 * @sideEffect Logs each unmapped native failure code once.
 */
export function useDerivedQueueStatus(input: { groups: Parameters<typeof deriveQueueStatus>[0]['groups']; native: NativeQueueInput | null; ready: boolean }): QueueStatus | null {
  const { groups, native, ready } = input;
  const { status, unknown } = useMemo(() => {
    if (!ready) return { status: null, unknown: [] as Array<string | null> };
    const seen: Array<string | null> = [];
    const derived = deriveQueueStatus({ groups, native, onUnknownCode: (code) => seen.push(code) });
    return { status: derived, unknown: seen };
  }, [groups, native, ready]);
  const reported = useRef(new Set<string | null>());
  useEffect(() => {
    for (const code of unknown) {
      if (reported.current.has(code)) continue;
      reported.current.add(code);
      reportUnknownFailureCode(code);
    }
  }, [unknown]);
  return status;
}
